import { useEffect, useMemo, useRef, useState } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useIsFetching, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation } from '@tanstack/react-router';

import { ConsolidatedFilters } from './filters-toolbar';
import { ExportPdfButton } from './export-pdf';
import { ConsolidatedOverviewPanel } from './overview-panel';
import { ConsolidatedCampaignsPanel } from './campaigns-panel';
import { AudiencePanel } from './audience-panel';
import { ConsolidatedCreativeAdsPanel } from './creative-ads-panel';
import { BudgetPanel } from './budget-panel';
import { ConsolidatedKeywordsTermsPanel } from './keywords-terms-panel';
import { ActivityPanel } from './activity-panel';
import { WidgetGrid, type WidgetConfig } from '@/components/glitch-ui/widget-grid';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/glitch-ui/spinner';
import { CONSOLIDATED_TABS, CONSOLIDATED_TAB_IDS, tabAvailability, resolveActiveTab } from '@/lib/performance/tabs';
import { isPerfQueryKey } from '@/lib/performance/refresh';
import type { PlatformScope } from '@/lib/performance/types';
import { campaignsQueryOptions } from '@/lib/query-options';
import {
  consolidatedPerformanceScopeAtom,
  selectedConsolidatedPerformanceTabAtom,
  consolidatedPerformanceWidgetLayoutAtom,
  consolidatedPerformanceCampaignIdsAtom,
  consolidatedPerformanceCampaignTypesAtom,
  consolidatedPerformanceFilterOrgAtom,
  organizationDataAtom,
  campaignsDataAtom,
  googleAdsAccountAtom,
  googleAdsAccountResolvedAtom,
} from '@/store';
import { useWidgetLayout } from '@/hooks/useWidgetLayout';
import { useTabPersistence } from '@/hooks/useTabPersistence';
import { tabButtonClass } from '@/components/glitch-ui/tab-card-panel';
import { RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';

// Per-tab widget layouts (drag-reorder + resize, persisted per user). The table
// tabs are one full-width widget each. Overview and Activity are omitted — they
// bring their own WidgetGrid and render outside this grid to avoid nested
// react-dnd providers. 'google-overview' is the layout for the Google detail
// grid inside the Overview panel (perf-v2's Overview widgets minus the
// performance-chart, which the top block's cross-platform chart already covers).
// Killed widgets: dropped even from persisted layouts (which save whole to
// user settings and would otherwise resurrect them forever). 'overview-metrics'
// died 09-08 — the blended chart tiles cover it with better semantics
// (dashes, axed ROAS, platform splits).
const RETIRED_WIDGET_IDS: ReadonlySet<string> = new Set(['overview-metrics', 'optimization-impact', 'next-steps']);

const CONSOLIDATED_DEFAULT_LAYOUTS: Record<string, WidgetConfig[]> = {
  // All ten default to half width (ruling 09-08): an even count pairs into a
  // tidy two-column grid; users resize from there.
  'google-overview': [
    { id: 'account-snapshot', width: 50, order: 0 },
    { id: 'problem-alerts', width: 50, order: 1 },
    { id: 'insights', width: 50, order: 2 },
    { id: 'money-context', width: 50, order: 5 },
    { id: 'type-efficiency', width: 50, order: 6 },
    { id: 'conversion-mix-donut', width: 50, order: 7 },
    { id: 'notable-search-terms', width: 50, order: 8 },
    { id: 'ai-actions', width: 50, order: 9 },
    { id: 'top-performers', width: 50, order: 10 },
  ],
  'google-campaigns': [
    { id: 'campaigns-chart', width: 100, order: 0 },
    { id: 'campaigns-table', width: 100, order: 1 },
  ],
  'google-ads': [{ id: 'ads-content', width: 100, order: 0 }],
  'google-keywords': [{ id: 'keywords-content', width: 100, order: 0 }],
  'google-search-terms': [{ id: 'search-terms-content', width: 100, order: 0 }],
  audience: [{ id: 'audience', width: 100, order: 0 }],
  budget: [{ id: 'budget', width: 100, order: 0 }],
  // The Meta detail grid inside the Overview panel, Meta-only scope (09-08).
  'meta-overview': [
    { id: 'meta-alerts', width: 50, order: 0 },
    { id: 'meta-cpl-trend', width: 50, order: 1 },
    { id: 'meta-top-campaigns', width: 50, order: 2 },
    { id: 'meta-lead-mix', width: 50, order: 3 },
    { id: 'meta-creative-leaderboard', width: 50, order: 4 },
    { id: 'meta-placement-mix', width: 50, order: 5 },
  ],
};

/**
 * The consolidated Performance shell (#5971, CP-3). Hosts the master platform
 * scope switch, the platform-aware tab bar, and the freshness header; renders the
 * active tab's panel. Per-tab content is wired in CP-6..CP-12 — the panels are
 * placeholders for now. Test-mode gated: non-test-mode users are sent to the
 * existing /performance page.
 */
export function ConsolidatedPerformance() {
  const [scope] = useAtom(consolidatedPerformanceScopeAtom);

  // #6279 — the filter atoms are global but a campaign selection is org-specific:
  // carried across an org switch it matches nothing and silently empties the
  // page. The org marker atom survives this component's withOrgReset remount,
  // so the comparison works even though the switch remounts us.
  const organizationData = useAtomValue(organizationDataAtom);
  const [filterOrg, setFilterOrg] = useAtom(consolidatedPerformanceFilterOrgAtom);
  const setSelectedCampaignIds = useSetAtom(consolidatedPerformanceCampaignIdsAtom);
  const setCampaignTypes = useSetAtom(consolidatedPerformanceCampaignTypesAtom);
  useEffect(() => {
    const orgId = organizationData?.id;
    if (orgId == null || filterOrg === orgId) return;
    if (filterOrg !== null) {
      setSelectedCampaignIds(new Set());
      setCampaignTypes(new Set(['all']));
    }
    setFilterOrg(orgId);
  }, [organizationData?.id, filterOrg, setSelectedCampaignIds, setCampaignTypes, setFilterOrg]);

  // #5996 — reflect the active tab in the URL hash (#campaigns), matching the
  // business page. Hash wins on refresh / back-forward; the atom persists it
  // across sidebar navigation. (Scope stays an atom — clean, #tab-only URLs.)
  const navigate = useNavigate();
  const location = useLocation();
  const { activeTab, handleTabChange } = useTabPersistence(
    selectedConsolidatedPerformanceTabAtom,
    CONSOLIDATED_TAB_IDS,
    'overview',
    (tabId, replace) => navigate({ to: '/performance-beta', hash: tabId, replace }),
    location.hash,
  );

  const availability = useMemo(
    () => new Map(CONSOLIDATED_TABS.map((t) => [t.id, tabAvailability(t, scope)] as const)),
    [scope],
  );
  const { getTabLayout, handleReorder, handleResize, resetTabLayout } = useWidgetLayout(
    consolidatedPerformanceWidgetLayoutAtom,
    CONSOLIDATED_DEFAULT_LAYOUTS,
    RETIRED_WIDGET_IDS,
  );

  const effectiveTab = resolveActiveTab(activeTab, scope);

  // One universal loading state while an org switch is settling (ruling
  // 09-09): until the account selection has hydrated, the new org's campaign
  // list has arrived, AND the page's atoms have caught up, neither the
  // mismatch banner nor the panels can know the truth — computing them from
  // the old org's atoms flashed the #6496 banner (and stale panel data) on
  // every switch. Three legs:
  // - resolved: the account selection has hydrated at all;
  // - campaignsPending: the (org, account) campaigns fetch has settled —
  //   per query key, so previously-visited orgs restore from cache (React
  //   Query dedupes this observer against the layout's own fetch);
  // - filterOrg: #6279's own org marker. The layout mirrors query → atoms in
  //   a passive effect, so a cache-hit switch still paints ONE frame with the
  //   old org's atoms before the effects flush; filterOrg updates in that
  //   same flush, so gating on it swallows exactly that stale frame.
  const googleAdsAccount = useAtomValue(googleAdsAccountAtom);
  const googleAdsAccountResolved = useAtomValue(googleAdsAccountResolvedAtom);
  const hasGoogleAccount = !!googleAdsAccount?.googleads_account_id;
  const { isPending: campaignsPending } = useQuery({
    ...campaignsQueryOptions(organizationData?.id, googleAdsAccount?.googleads_account_id),
    enabled: !!organizationData?.id && hasGoogleAccount,
  });
  const orgLoading =
    !googleAdsAccountResolved ||
    organizationData?.id == null ||
    filterOrg !== organizationData.id ||
    (hasGoogleAccount && campaignsPending);

  // #6496 — the campaigns list is scoped to the caller's SELECTED Google Ads
  // account, so when the organization's campaigns run on a different account
  // every Google report here is scoped to nothing and the tabs sit empty with
  // no explanation (prod: org campaigns on one account, viewer selected
  // another). Say so instead of showing silent zeros.
  const glitchCampaigns = useAtomValue(campaignsDataAtom);
  const accountMismatch =
    !orgLoading &&
    glitchCampaigns.length === 0 && ((organizationData as { campaign_count?: number } | null)?.campaign_count ?? 0) > 0;

  // TabCardPanel's sentinel pattern (#5348): a 0-height marker above the
  // sticky row; once it scrolls out of view the row is pinned and gets the
  // drop shadow.
  const stuckSentinelRef = useRef<HTMLDivElement>(null);
  const [tabsStuck, setTabsStuck] = useState(false);
  useEffect(() => {
    const sentinel = stuckSentinelRef.current;
    if (!sentinel || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(([entry]) => setTabsStuck(!entry.isIntersecting), { threshold: 0 });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  return (
    <div>
      {/* Sticky folder-tab row — pinned to the top of the page scroller like the
          other tabbed pages (TabCardPanel). Flowing content below lets the tabs
          stick instead of the whole surface scrolling (#5971). */}
      {/* The page's 6px gap lives INSIDE the sticky strip so it survives
          pinning (ruling 09-08: never flush to the viewport edge, unlike
          TabCardPanel's flush default), plus the sentinel-driven stuck
          shadow from the house pattern. */}
      <div ref={stuckSentinelRef} aria-hidden className="h-px w-full" />
      <div
        className={cn(
          'sticky top-0 z-20 bg-[#fafafb] pt-1.5 transition-shadow duration-200',
          tabsStuck && 'shadow-[0_6px_8px_-6px_rgba(15,23,42,0.18)]',
        )}
      >
        <div className="flex items-end gap-1">
          {/* No scrollbar on the nav, ever (ruling 2026-09-08): overflow stays
              reachable by trackpad but never draws a scroll strip. */}
          <div className="flex min-w-0 items-end gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {CONSOLIDATED_TABS.map((tab) => {
            const avail = availability.get(tab.id)!;
            const isActive = tab.id === effectiveTab;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                disabled={!avail.available}
                title={avail.available ? undefined : avail.reason}
                onClick={() => avail.available && handleTabChange(tab.id)}
                className={cn(
                  tabButtonClass(isActive),
                  'whitespace-nowrap',
                  !avail.available && 'cursor-not-allowed opacity-40 hover:bg-gray-100 hover:text-gray-700',
                )}
              >
                {tab.label}
              </button>
            );
          })}
          </div>
          {/* #6500 — the filters live inline with the tabs, not in the page
              body: one row owns navigation and narrowing, and the content card
              starts directly with data. pb-1.5 keeps the controls clear of the
              active tab's card join. */}
          {/* pb-1 keeps the controls block (36+4px) strictly shorter than the
              42px folder tabs, so the buttons can never set the row height
              and push the tabs down (09-08). */}
          <div className="ml-auto flex flex-shrink-0 items-center gap-2 pb-1 pl-3">
            <ConsolidatedFilters scope={scope} activeTab={effectiveTab} />
            <ExportPdfButton scope={scope} />
            <PerfRefreshButton />
          </div>
        </div>
      </div>

      {accountMismatch && (
        <div className="mb-3 border-l-4 border-[#FDE68A] bg-[#FEF3C7] px-4 py-3 text-sm text-[#A16207]">
          This organization's campaigns run on a different Google Ads account than the selected one, so no
          performance can be shown. Switch accounts from the account menu to see them.
        </div>
      )}
      {/* White content card the active tab connects into (#5971): filter toolbar
          at the top, panels below. The active folder tab's transparent bottom
          border merges into this card. */}
      <div className="-mt-px rounded-b-lg rounded-tr-lg border border-gray-200 bg-white">
        <div className="p-4">
          {orgLoading ? (
            <div className="flex h-64 items-center justify-center">
              <Spinner size="lg" className="text-gray-400" />
              <span className="ml-3 text-sm text-gray-500">Loading performance data…</span>
            </div>
          ) : (
            /* MERGE-REVIEW: dev had a MetaComingSoon placeholder for scope === 'meta' (its 1.13
               port lacked Meta wiring); release has real Meta panels, so TabPanel renders for all
               scopes here. */
            <TabPanel
              tabId={effectiveTab}
              scope={scope}
              getTabLayout={getTabLayout}
              onReorder={handleReorder}
              onResize={handleResize}
              onResetTab={resetTabLayout}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Manual sync for the whole surface (#6293): invalidates every performance
 * query (isPerfQueryKey) so all mounted widgets refetch; unmounted tabs
 * refetch on next visit. Data is otherwise served from a 1-hour cache.
 */
function PerfRefreshButton() {
  const queryClient = useQueryClient();
  const fetching = useIsFetching({ predicate: (q) => isPerfQueryKey(q.queryKey) }) > 0;
  return (
    <Button
      variant="outline"
      className="h-9 text-gray-900"
      disabled={fetching}
      onClick={() => queryClient.invalidateQueries({ predicate: (q) => isPerfQueryKey(q.queryKey) })}
    >
      {/* Constant label: the Refresh/Refreshing swap resized the button and
          shoved the whole control row sideways (ruling 2026-09-08). The
          spinning icon carries the in-flight signal. */}
      <RefreshCw className={cn('mr-2 h-4 w-4', fetching && 'animate-spin')} />
      Refresh
    </Button>
  );
}

function TabPanel({
  tabId,
  scope,
  getTabLayout,
  onReorder,
  onResize,
  onResetTab,
}: {
  tabId: string;
  scope: PlatformScope;
  getTabLayout: (tabId: string) => WidgetConfig[];
  onReorder: (tabId: string, layout: WidgetConfig[]) => void;
  onResize: (tabId: string, id: string, width: 33 | 50 | 66 | 100) => void;
  onResetTab: (tabId: string) => void;
}) {
  // Overview and Activity bring their own WidgetGrid (react-dnd), so they render
  // outside this grid — only one dnd provider is ever mounted at a time.
  if (tabId === 'overview') {
    return (
      <ConsolidatedOverviewPanel
        scope={scope}
        getTabLayout={getTabLayout}
        onReorder={onReorder}
        onResize={onResize}
        onResetTab={onResetTab}
      />
    );
  }
  if (tabId === 'activity') return <ActivityPanel />;
  // Campaigns is hybrid by scope (#5995): Google reuses perf-v2's CampaignsTab
  // (its own WidgetGrid), Meta / All keep the merged CampaignsPanel table.
  if (tabId === 'campaigns') {
    return (
      <ConsolidatedCampaignsPanel
        scope={scope}
        getTabLayout={getTabLayout}
        onReorder={onReorder}
        onResize={onResize}
      />
    );
  }
  // Creative & Ads is hybrid by scope (#5995): Google reuses perf-v2's AdsTab
  // (its own WidgetGrid), Meta / All keep the merged CreativeAdsPanel table.
  if (tabId === 'creative-ads') {
    return (
      <ConsolidatedCreativeAdsPanel
        scope={scope}
        getTabLayout={getTabLayout}
        onReorder={onReorder}
        onResize={onResize}
      />
    );
  }
  // Keywords & Terms is Google-only (the shell gates it off under Meta), so no
  // scope branch — it reuses perf-v2's KeywordsTab + SearchTermsTab (its own
  // WidgetGrid) fed by the shared usePerformanceData hook (#5995).
  if (tabId === 'keywords-terms') {
    return (
      <ConsolidatedKeywordsTermsPanel
        getTabLayout={getTabLayout}
        onReorder={onReorder}
        onResize={onResize}
      />
    );
  }

  return (
    <WidgetGrid
      layout={getTabLayout(tabId)}
      onReorder={(layout) => onReorder(tabId, layout)}
      onResize={(id, width) => onResize(tabId, id, width)}
    >
      {(widgetId) => renderConsolidatedWidget(widgetId, scope)}
    </WidgetGrid>
  );
}

function renderConsolidatedWidget(widgetId: string, scope: PlatformScope) {
  switch (widgetId) {
    case 'audience':
      return <AudiencePanel scope={scope} />;
    case 'budget':
      return <BudgetPanel scope={scope} />;
    default:
      return null;
  }
}
